import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisclosure, useColorModeValue } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useToast } from '@/client/hooks/useToast';
import { useRuntimeStats, useProfiles, useCollectProfile, useProfileAnalysis } from '@/lib/sdk/profiling/queries';
import { ProfileType, ProfileStatus } from '@/lib/sdk/profiling/types';

interface CollectFormData {
  profile_type: ProfileType;
  duration_seconds: number;
  description: string;
}

export function useProfilingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);

  const { data: runtimeStats, isLoading: isLoadingStats } = useRuntimeStats();
  const { data: profilesData, isLoading: isLoadingProfiles } = useProfiles({ limit: 5 });
  const { mutate: collectProfile, isPending: isCollecting } = useCollectProfile();
  const { data: analysisData, isLoading: isLoadingAnalysis } = useProfileAnalysis(selectedProfileId || '');

  const cardGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  const { register, handleSubmit: rhfHandleSubmit, reset, formState: { errors } } = useForm<CollectFormData>({
    defaultValues: { profile_type: ProfileType.CPU, duration_seconds: 30, description: '' },
  });

  const isSuperAdmin = user?.is_super_admin === true;

  const latestProfile = profilesData?.items?.[0];

  // Auto-select latest completed profile (render-time conditional update, same as original)
  if (latestProfile && latestProfile.status === ProfileStatus.COMPLETED && !selectedProfileId) {
    setSelectedProfileId(latestProfile.id);
  }

  const profileTypeOptions = Object.values(ProfileType).map(type => ({
    value: type,
    label: type.charAt(0).toUpperCase() + type.slice(1),
  }));

  const handleCollectProfile = (data: CollectFormData) => {
    collectProfile(
      { profile_type: data.profile_type, duration_seconds: data.duration_seconds, description: data.description },
      {
        onSuccess: (profile) => { toast.success(`${profile.profile_type.toUpperCase()} profile collection started`); onClose(); reset(); },
        onError: (error: Error) => toast.error(error.message || 'Failed to collect profile'),
      }
    );
  };

  const onCollectSubmit = rhfHandleSubmit(handleCollectProfile);

  const openForType = (type: ProfileType) => {
    reset({ profile_type: type, duration_seconds: 30, description: '' });
    onOpen();
  };

  return {
    navigate, isSuperAdmin,
    isOpen, onClose,
    runtimeStats, isLoadingStats,
    profilesData, isLoadingProfiles,
    analysisData, isLoadingAnalysis,
    isCollecting,
    selectedProfileId, latestProfile,
    cardGradient, profileTypeOptions,
    register, errors,
    onCollectSubmit, openForType,
  };
}
